import { createBrowserRouter, Navigate, Outlet } from 'react-router-dom';
import { useAuthStore } from '../store/authStore';

// Lazy load pages for code splitting
import { lazy, Suspense } from 'react';
import LoadingSpinner from '../components/common/LoadingSpinner';

const LoginPage = lazy(() => import('../pages/LoginPage'));
const RegisterPage = lazy(() => import('../pages/RegisterPage'));
const ForgotPasswordPage = lazy(() => import('../pages/ForgotPasswordPage'));
const ResetPasswordPage = lazy(() => import('../pages/ResetPasswordPage'));
const VerifyEmailPage = lazy(() => import('../pages/VerifyEmailPage'));
const TripPlannerPage = lazy(() => import('../pages/TripPlannerPage'));
const TripPlanViewPage = lazy(() => import('../pages/TripPlanViewPage'));
const PlansListPage = lazy(() => import('../pages/PlansListPage'));

// Suspense wrapper for lazy components
function SuspenseWrapper({ children }: { children: React.ReactNode }) {
  return <Suspense fallback={<LoadingSpinner fullScreen />}>{children}</Suspense>;
}

// Protected route wrapper
function ProtectedRoute() {
  const isAuthenticated = useAuthStore((state) => state.isAuthenticated);

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  return (
    <SuspenseWrapper>
      <Outlet />
    </SuspenseWrapper>
  );
}

// Public route wrapper (redirect to home if already authenticated)
function PublicRoute() {
  const isAuthenticated = useAuthStore((state) => state.isAuthenticated);

  if (isAuthenticated) {
    return <Navigate to="/" replace />;
  }

  return (
    <SuspenseWrapper>
      <Outlet />
    </SuspenseWrapper>
  );
}

// Open route wrapper — always reachable regardless of auth state. Used for the
// email-link flows (verify-email must run its logic; reset-password may be
// opened by an already-logged-in user).
function OpenRoute() {
  return (
    <SuspenseWrapper>
      <Outlet />
    </SuspenseWrapper>
  );
}

export const router = createBrowserRouter([
  // Public routes (redirect away if already authenticated)
  {
    element: <PublicRoute />,
    children: [
      {
        path: '/login',
        element: <LoginPage />,
      },
      {
        path: '/register',
        element: <RegisterPage />,
      },
      {
        path: '/forgot-password',
        element: <ForgotPasswordPage />,
      },
    ],
  },

  // Open routes for email-link flows (reachable regardless of auth state)
  {
    element: <OpenRoute />,
    children: [
      {
        path: '/reset-password',
        element: <ResetPasswordPage />,
      },
      {
        path: '/verify-email',
        element: <VerifyEmailPage />,
      },
    ],
  },

  // Protected routes
  {
    element: <ProtectedRoute />,
    children: [
      {
        path: '/',
        element: <PlansListPage />,
      },
      {
        path: '/new',
        element: <TripPlannerPage />,
      },
      // Legacy path kept for old links/bookmarks
      {
        path: '/plans',
        element: <Navigate to="/" replace />,
      },
      {
        path: '/plan/:id',
        element: <TripPlanViewPage />,
      },
    ],
  },

  // Catch all - redirect to home
  {
    path: '*',
    element: <Navigate to="/" replace />,
  },
]);
